import { uniqWith } from 'es-toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import AddItemIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Checkbox, IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { produce } from 'immer';

import { INDETERMINATE, TooltipIconButton } from '@paragon/client-tools';
import { TrussEnvelope } from '@paragon/contract-library';
import { distanceGreaterThan } from '@paragon/geometry-library';
import { DistanceField } from '@paragon/material';
import { forceArray } from '@paragon/utilities';

import {
  calculateTrussEnvelopeCapSetGroupings,
  CapSetLevel,
  CapSetState,
  capSetStatesAreEqual,
} from '../../../../library/layout-geometry/truss-envelopes/capSets';
import { editCapSet } from '../../../../store/actions/layout/trussEnvelopeActions';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { LabeledBox } from './LabeledBox';

export function CapSetProperties(props: { envelopes: TrussEnvelope | readonly TrussEnvelope[]; viewOnly: boolean }) {
  const dispatch = useAppDispatch();

  const envelopes = useMemo(() => forceArray(props.envelopes), [props.envelopes]);

  const capSetGroups = useAppSelector(calculateTrussEnvelopeCapSetGroupings);
  const commonState = useMemo(() => {
    const capSetStates = uniqWith(
      envelopes.map(({ guid }) => capSetGroups[guid].capSetState),
      capSetStatesAreEqual,
    );
    return capSetStates.length === 1 ? capSetStates[0] : null;
  }, [envelopes, capSetGroups]);
  const [previousCommonState, setPreviousCommonState] = useState(commonState);

  const [state, update] = useState(commonState);
  const setState = useCallback(
    (updater: CapSetUpdater | CapSetState | null) =>
      update((oldState) => {
        if (typeof updater === 'object' && updater != null) return updater;
        if (oldState == null || updater == null) throw new Error('Expected cap set state to be non-null');
        return updater(oldState);
      }),
    [update],
  );

  // Any time the backing data changes (such as envelopes being (de)selected), reset the UI
  if (
    (previousCommonState != null && commonState != null && !capSetStatesAreEqual(previousCommonState, commonState)) ||
    (previousCommonState == null) !== (commonState == null)
  ) {
    setPreviousCommonState(commonState);
    update(commonState);
  }

  const disabled = props.viewOnly || state == null;
  const changesToApply = commonState != null && state != null && !capSetStatesAreEqual(commonState, state);

  return (
    <LabeledBox title="Cap set" sx={{ margin: 0 }} contentSx={{ padding: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', columnGap: 1 }}>
        <DistanceField
          label="Base Elevation"
          canBeIndeterminate
          value={commonState?.baseElevation ?? INDETERMINATE}
          disabled
        />
        <Tooltip disableInteractive title="Generate envelope at this level">
          <Checkbox
            checked={state?.generateBase}
            indeterminate={disabled}
            onChange={({ target }) => setState(changeGenerateBase(target.checked))}
            disabled={disabled}
          />
        </Tooltip>
        <IconButton sx={{ visibility: 'hidden' }}>
          <DeleteIcon />
        </IconButton>
        {state?.additionalLevels.map((level, index) => (
          <>
            <DistanceField
              label="Cap Height Offset"
              value={level.elevation}
              min={0}
              onSubmit={(elevation) => setState(editLevel(index, { elevation }))}
              disabled={disabled}
            />
            <Tooltip disableInteractive title="Generate envelope at this level">
              <Checkbox
                checked={level.generate}
                onChange={({ target }) => setState(editLevel(index, { generate: target.checked }))}
                disabled={disabled}
              />
            </Tooltip>
            <TooltipIconButton
              title="Remove cap height"
              onClick={() => setState(deleteLevel(index))}
              disabled={disabled}
            >
              <DeleteIcon />
            </TooltipIconButton>
          </>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <TooltipIconButton title="Add cap height" onClick={() => setState(addLevel())} disabled={disabled}>
          <AddItemIcon />
        </TooltipIconButton>
        <Button
          sx={{ margin: 1, marginLeft: 'auto' }}
          variant="outlined"
          disabled={disabled || !changesToApply}
          onClick={() => setState(commonState)}
        >
          Cancel
        </Button>
        <Button
          sx={{ margin: 1 }}
          variant="contained"
          disabled={disabled || !changesToApply || !changesAreValid(state)}
          onClick={async () => await dispatch(editCapSet(envelopes, state!))}
        >
          Apply
        </Button>
      </Box>
    </LabeledBox>
  );
}

type CapSetUpdater = (oldState: CapSetState) => CapSetState;

const NEW_LEVEL_OFFSET = 120; // 10'
const addLevel = (): CapSetUpdater =>
  produce((draft) => {
    draft.additionalLevels.push({ elevation: NEW_LEVEL_OFFSET, generate: true });
  });

const deleteLevel = (index: number): CapSetUpdater =>
  produce((draft) => {
    draft.additionalLevels.splice(index, 1);
  });

const editLevel = (index: number, edits: Partial<CapSetLevel>): CapSetUpdater =>
  produce((draft) => {
    draft.additionalLevels[index] = { ...draft.additionalLevels[index], ...edits };
  });

const changeGenerateBase = (generateBase: boolean): CapSetUpdater =>
  produce((draft) => {
    draft.generateBase = generateBase;
  });

const changesAreValid = (state: CapSetState | null): boolean => {
  if (state == null) return false;

  const atLeastOneEnvelope = state.generateBase || state.additionalLevels.some(({ generate }) => generate);
  if (!atLeastOneEnvelope) return false;

  const increasingElevation = state.additionalLevels.every(({ elevation }) => distanceGreaterThan(elevation, 0));
  if (!increasingElevation) return false;

  return true;
};
